import { createSelector } from "@reduxjs/toolkit";
import { FILE_SOURCES } from "layout/documentUploader/constants";
import { selectUsername } from "slices/app/selectors";
import { selectIsLocalFileSharingEnabled } from "slices/generativeAi/selectors";
import { selectSharepointEnabled } from "slices/sharepoint/selectors";

export const selectCollections = (state) => state.collections?.list || [];

export const selectSelectedCollection = (state) => state.collections?.selected;

export const selectSelectedCollectionName = (state) =>
  state.collections?.selected?.name;

export const selectSelectedFiles = (state) =>
  state.collections?.selected?.files || [];

export const selectUsers = (state) => state.collections?.users || [];

export const selectIsCollectionsEnabled = (state) =>
  state.collections.isEnabled;

export const selectCanShareCollection = createSelector(
  [
    selectSelectedCollection,
    selectIsLocalFileSharingEnabled,
    selectSharepointEnabled,
  ],
  (selectedCollection, isLocalFileSharingEnabled, isSharepointEnabled) => {
    if (!selectedCollection) return true;

    const hasLocalFiles = selectedCollection.files?.some(
      (file) => file.source === FILE_SOURCES.LOCAL
    );

    // If there are no local files, SharePoint is disabled, and local file sharing is disabled, return false
    if (!hasLocalFiles && !isSharepointEnabled && !isLocalFileSharingEnabled)
      return false;

    return !hasLocalFiles || isLocalFileSharingEnabled;
  }
);

export const selectOtherUsers = createSelector(
  [selectUsers, selectUsername],
  (users, username) => users.filter((user) => user.username !== username)
);

export const selectSelectedCollections = (state) =>
  state.collections?.selectedCollections || [];

export const selectSelectedCollectionsCount = (state) =>
  state.collections?.selectedCollections?.length || 0;

export const selectAllSelectedDocumentHashes = createSelector(
  [selectSelectedCollections],
  (collections) => {
    const allFiles = collections.flatMap((c) => c.files || []);
    const seen = new Set();
    return allFiles
      .filter((file) => {
        if (seen.has(file.hash)) return false;
        seen.add(file.hash);
        return true;
      })
      .map((file) => file.hash);
  }
);

export const selectHasFilesInSelectedCollections = createSelector(
  [selectSelectedCollections],
  (collections) => collections.some((c) => c.files?.length > 0)
);

export const selectHasCategoriesColumn = createSelector(
  [selectCollections],
  (collections) => collections.some((c) => c.collectionCategories?.length > 0)
);
